import { useState, useEffect, useCallback } from "react";
import { useRenderByIdQuery } from "@/api/renders";
import type { RenderJob, RenderStatus } from "@/types/render";

interface RenderProgress {
  job: RenderJob | null;
  status: RenderStatus | null;
  isComplete: boolean;
  isFailed: boolean;
  isRendering: boolean;
  progress: number;
  error: string | null;
  refetch: () => void;
}

/**
 * Polls a render job's status and returns progress information.
 * Automatically stops polling when the job reaches a terminal state.
 */
export function useRenderProgress(renderJobId: string | null): RenderProgress {
  const [shouldPoll, setShouldPoll] = useState(true);

  const { data: job, refetch } = useRenderByIdQuery(
    renderJobId ?? "",
    shouldPoll && !!renderJobId,
  );

  const isComplete = job?.status === "complete";
  const isFailed = job?.status === "error";
  const isRendering = job?.status === "rendering" || job?.status === "queued";

  useEffect(() => {
    if (isComplete || isFailed) {
      setShouldPoll(false);
    }
  }, [isComplete, isFailed]);

  // Reset polling when renderJobId changes
  useEffect(() => {
    if (renderJobId) {
      setShouldPoll(true);
    }
  }, [renderJobId]);

  const manualRefetch = useCallback(() => {
    void refetch();
  }, [refetch]);

  return {
    job: job ?? null,
    status: job?.status ?? null,
    isComplete,
    isFailed,
    isRendering,
    progress: job?.progress ?? 0,
    error: job?.error_message ?? null,
    refetch: manualRefetch,
  };
}
